import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

/** How long a copy outcome stays shown before the control returns to idle. */
export const COPY_STATUS_RESET_MS = 2000;

/** The outcome of the last copy, while it is still being shown. */
export type CopyStatus = 'idle' | 'copied' | 'failed';

const STATUS_MESSAGE_IDS: Record<CopyStatus, string> = {
    idle: 'copyToClipboard',
    copied: 'copied',
    failed: 'copyFailed',
};

export interface CopyToClipboard {
    /** Write `text` to the clipboard; resolves once the outcome is known. */
    copy: (text: string) => Promise<void>;
    status: CopyStatus;
    /** What a copy control says in `status`: the action, or its outcome. */
    label: string;
    /** Return to idle before the outcome times out. */
    reset: () => void;
}

/**
 * Writes text to the clipboard and reports whether the write landed.
 *
 * `copied` is reported only once the browser accepts the write. A refused
 * write (a non-secure origin, a denied permission) reports `failed`: the user
 * pastes on the strength of the confirmation, and a false one has them act on
 * whatever the clipboard held before.
 */
export function useCopyToClipboard(): CopyToClipboard {
    const intl = useIntl();
    const [status, setStatus] = useState<CopyStatus>('idle');
    const resetTimerRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => () => clearTimeout(resetTimerRef.current), []);

    const reset = useCallback(() => {
        clearTimeout(resetTimerRef.current);
        setStatus('idle');
    }, []);

    const copy = useCallback(async (text: string) => {
        clearTimeout(resetTimerRef.current);
        try {
            await navigator.clipboard.writeText(text);
            setStatus('copied');
        } catch {
            setStatus('failed');
        }
        resetTimerRef.current = setTimeout(
            () => setStatus('idle'),
            COPY_STATUS_RESET_MS
        );
    }, []);

    return {
        copy,
        status,
        label: intl.formatMessage({ id: STATUS_MESSAGE_IDS[status] }),
        reset,
    };
}
